/**
 * @file SupportModePanel.tsx
 * @description Panel that lets support agents toggle impersonation mode and
 *   view or update the profile of the employee they are assisting.
 * @author Sakshi Bhise
 */

// External packages
import React, { useEffect, useState } from "react";
import { PrimaryButton } from "@fluentui/react";
import { ToggleButton } from "@teva/ui";
import { FiUserCheck, FiUserX } from "react-icons/fi";

// Internal imports
import styles from "./ImpersonateMenu.module.css";
import { ImpersonateMenu } from "./ImpersonateMenu";

interface SupportModePanelProps {
    isActive: boolean;
    userDetails: { email: string; country: string; workSite: string };
    onDeactivate: () => void;
    onImpersonate: () => void;
    settings: { Email: string };
    onSettingsChange: (event: any) => void;
    error?: string;
    onResetForm: () => void;
    isImpersonateLoading?: boolean;
}

/**
 * Panel that lets a support agent activate/deactivate impersonation mode and
 * view or update details for the employee they are currently assisting.
 *
 * @param {object} props
 * @param {boolean} props.isActive - Whether impersonation mode is currently active.
 *   Example: true
 * @param {{ email: string; country: string; workSite: string }} props.userDetails - Profile details of the impersonated user.
 *   Example: { email: "john@teva.com", country: "US", workSite: "Remote" }
 * @param {() => void} props.onDeactivate - Callback to deactivate impersonation mode.
 * @param {() => void} props.onImpersonate - Callback to trigger the impersonation action.
 * @param {{ Email: string }} props.settings - Current impersonation form settings.
 *   Example: { Email: "jane@teva.com" }
 * @param {(event: any) => void} props.onSettingsChange - Callback fired when the settings form changes.
 * @param {string} [props.error] - Optional error message to display in the email entry view.
 *   Example: "User not found."
 * @param {() => void} props.onResetForm - Callback to reset the impersonation form to its initial state.
 * @param {boolean} [props.isImpersonateLoading] - Whether an impersonation request is in progress.
 *   Example: false
 * @returns {JSX.Element} Either the email-entry sub-panel or the impersonation status view.
 */
export const SupportModePanel: React.FC<SupportModePanelProps> = ({
    isActive,
    userDetails,
    onDeactivate,
    onImpersonate,
    settings,
    onSettingsChange,
    error,
    onResetForm,
    isImpersonateLoading = false,
}) => {
    const [showEmailEntry, setShowEmailEntry] = useState(false);

    // Switch back to status view when impersonation becomes active (successful start)
    useEffect(() => {
        if (isActive) setShowEmailEntry(false);
    }, [isActive]);

    /**
     * Handles the toggle button state change for enabling or disabling support mode.
     * Prevents any action while an impersonation request is already in flight.
     *
     * @param isChecked - The new toggle state. True opens email entry; false deactivates.
     *   Example: true
     * @returns void
     */
    const handleToggle = (isChecked: boolean) => {
        if (isImpersonateLoading) return;
        if (isChecked) {
            onResetForm();
            setShowEmailEntry(true);
        } else {
            onDeactivate();
        }
    };

    // ---- email entry view ----
    if (showEmailEntry) {
        return (
            <div style={{ padding: 16 }}>
                <ImpersonateMenu
                    settings={settings}
                    onSettingsChange={onSettingsChange}
                    onImpersonate={onImpersonate}
                    onExitImpersonation={() => {
                        onResetForm();
                        setShowEmailEntry(false);
                    }}
                    error={error}
                    isLoading={isImpersonateLoading}
                />
            </div>
        );
    }

    // ---- status view ----
    return (
        <div style={{ padding: 16 }}>
            <div className={styles.statusRow}>
                <span style={{ fontWeight: 500 }}>{isActive ? "Active" : "Inactive"}</span>
                <span className={styles.status}>
                    {isActive
                        ? <FiUserCheck style={{ color: "green", fontSize: 16 }} />
                        : <FiUserX style={{ color: "red", fontSize: 16 }} />
                    }
                    <ToggleButton isChecked={isActive} onChange={handleToggle} isDisabled={isImpersonateLoading} />
                </span>
            </div>

            {isActive ? (
                <>
                    <PrimaryButton
                        className={styles.updateBtn}
                        onClick={() => { onResetForm(); setShowEmailEntry(true); }}
                        styles={{ root: { width: "100%", marginBottom: 8, borderRadius: 8 } }}
                    >
                        Update User
                    </PrimaryButton>

                    <div className={styles.userDetailsBox}>
                        <span className={styles.detailsTitle}>Supported User:</span>
                        <div className={styles.detailRow}><strong>COUNTRY</strong><br />{userDetails.country}</div>
                        <div className={styles.detailRow}><strong>WORK SITE</strong><br />{userDetails.workSite}</div>
                        <div className={styles.detailRow}><strong>EMAIL</strong><br />{userDetails.email}</div>
                    </div>

                    <div className={styles.infoBox}>
                        Answers are now tailored to the supported employee's profile but will remain in your chat history alone.
                    </div>
                </>
            ) : (
                <div style={{ background: "#f5f8ff", padding: 8, borderRadius: 6, fontSize: 13, color: "#333" }}>
                    Toggle on Support Mode to view AskHR AI information tailored to the employee you're assisting.
                </div>
            )}
        </div>
    );
};
